/**
 * AttachmentPreviewBar Component
 * Shows a horizontal list of attached files below the textarea with remove buttons
 */

import { X, File, Image, FileText } from 'lucide-react';
import type { AttachmentMeta } from '@/types/websocket.types';

const iconForType = (contentType?: string) => {
  if (!contentType) return File;
  if (contentType.startsWith('image/')) return Image;
  if (contentType === 'application/pdf' || contentType.includes('document') || contentType.includes('text'))
    return FileText;
  return File;
};

const formatSize = (bytes?: number) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
};

interface AttachmentPreviewBarProps {
  attachments: AttachmentMeta[];
  onRemove: (index: number) => void;
}

export const AttachmentPreviewBar = ({ attachments, onRemove }: AttachmentPreviewBarProps) => {
  if (attachments.length === 0) return null;

  return (
    <div className='flex flex-wrap gap-1.5 w-full px-1'>
      {attachments.map((att, i) => {
        const Icon = iconForType(att.content_type);
        const size = formatSize(att.size);
        return (
          <div
            key={`${att.name}-${i}`}
            className='inline-flex items-center gap-1.5 rounded-lg bg-gray-50 border border-gray-200 px-2.5 py-1.5 text-xs text-gray-700 max-w-[200px] group'
          >
            <Icon className='h-3.5 w-3.5 shrink-0 text-gray-400' />
            <span className='truncate'>{att.name}</span>
            {size && <span className='text-gray-400 shrink-0'>({size})</span>}
            <button
              type='button'
              onClick={() => onRemove(i)}
              className='ml-0.5 shrink-0 rounded-full p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors'
              title='Remove attachment'
            >
              <X className='h-3 w-3' />
            </button>
          </div>
        );
      })}
    </div>
  );
};
